import React, { useState, useRef } from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { QuoteBlock } from '../../../ui/QuoteBlock/QuoteBlock.jsx'

/**
 * KeyTakeawayCard — session-takeaway (catalog #4, CORE).
 *
 * Single sentence, 120 soft / 160 hard char limit. Rendered via
 * QuoteBlock variant="berry". SingleInput for edit.
 *
 * Empty-state shows the prompt. Filled state shows the QuoteBlock.
 * Over-limit state shows a rust char counter.
 */

const SOFT_LIMIT = 120
const HARD_LIMIT = 160

export function KeyTakeawayCard({ text = '', onChange, disabled = false }) {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef(null)

  const hasContent = text.length > 0
  const isOver = text.length > SOFT_LIMIT
  const isHard = text.length > HARD_LIMIT

  function handleFocus() {
    if (!disabled) setEditing(true)
  }

  function handleBlur() {
    setEditing(false)
  }

  const counterColor = isHard ? 'var(--color-rust-600, #c0392b)' : isOver ? 'var(--color-mustard-600, #b7770d)' : 'var(--color-text-faint, #999)'

  return (
    <CardFrame
      kind="session"
      icon="◇"
      iconClass="berry"
      title="Key takeaway"
      prompt="What's the one thing worth remembering?"
      state={disabled ? 'locked' : isHard ? 'error' : hasContent ? 'filled' : 'empty'}
      data-testid="session-card-key-takeaway"
      className="session-card"
    >
      {hasContent && !editing ? (
        <QuoteBlock
          variant="berry"
          text={text}
          showQuotes
          onClick={!disabled ? handleFocus : undefined}
          role={!disabled ? 'button' : undefined}
          tabIndex={!disabled ? 0 : undefined}
          aria-label={`Key takeaway: ${text}. Click to edit.`}
          onKeyDown={!disabled ? (e) => e.key === 'Enter' && handleFocus() : undefined}
        />
      ) : (
        <textarea
          ref={inputRef}
          className="session-card__textarea session-card__textarea--centered"
          value={text}
          maxLength={HARD_LIMIT}
          disabled={disabled}
          placeholder="The one thing worth remembering from this session…"
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label="Key takeaway"
          rows={3}
        />
      )}
      {!disabled && (
        <div
          className="session-card__char-counter"
          style={{ color: counterColor }}
          aria-live="polite"
          aria-label={`${text.length} of ${SOFT_LIMIT} characters`}
        >
          {text.length}/{SOFT_LIMIT}
        </div>
      )}
    </CardFrame>
  )
}
